// Centralized WebRTC ICE configuration for every call surface (agent voice widget,
// public customer call page, and internal chat calls). Previously each of these
// hardcoded its own copy with a SINGLE UDP-only TURN relay — which meant that on any
// network that blocks outbound UDP (corporate firewalls, guest WiFi, some mobile
// carriers) there was NO working candidate and the call hung forever on "connecting".
//
// This config offers UDP → TCP → TLS(443) in order so the browser can always find a
// path out:
//   - STUN over UDP (fast path when the network is permissive)
//   - TURN over UDP  (relay when both peers are behind symmetric NAT)
//   - TURN over TCP  (relay when UDP is blocked but arbitrary TCP is allowed)
//   - TURN over TLS on 443 (relay on the most locked-down networks — looks like HTTPS)
//
// NOTE: the TCP and TLS entries only actually relay if the coturn box exposes those
// ports (tcp 3478 + tls-listening-port 443). Listing them is harmless if it doesn't —
// the browser just discards candidates it can't reach — and it future-proofs the moment
// coturn is opened up. Keep this file as the single source of truth: never re-inline an
// ICE server list in a component.

const TURN_HOST = '172.238.210.176';
const TURN_USER = 'ayudatecno';
const TURN_CRED = 'AyudaTecnoTURN2026!';

export const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  // UDP relay (works today; the only path the old config offered)
  { urls: `turn:${TURN_HOST}:3478?transport=udp`, username: TURN_USER, credential: TURN_CRED },
  // TCP relay — survives UDP-blocking networks
  { urls: `turn:${TURN_HOST}:3478?transport=tcp`, username: TURN_USER, credential: TURN_CRED },
  // TLS relay on 443 — survives the most restrictive firewalls (indistinguishable from HTTPS)
  { urls: `turns:${TURN_HOST}:443?transport=tcp`, username: TURN_USER, credential: TURN_CRED },
];

// Full RTCPeerConnection configuration. iceCandidatePoolSize pre-gathers candidates so
// the first negotiation is faster and less likely to time out.
export const RTC_CONFIG = {
  iceServers: ICE_SERVERS,
  iceCandidatePoolSize: 4,
};

// How long to sit in "connecting" before we stop pretending and surface an honest
// "couldn't connect" state (with a retry). Prevents the infinite silent spinner.
export const CONNECT_TIMEOUT_MS = 25000;

// Fetch the live ICE config from the backend (which layers in managed TURN — Cloudflare
// or a static provider — on top of the self-hosted relay). The agent side calls this;
// the customer side gets its config inline from the public-token response instead.
// Always falls back to the static RTC_CONFIG so a fetch hiccup never blocks a call.
export async function fetchAgentIceConfig(api) {
  try {
    const { data } = await api.get('/voice-calls/ice-config', { timeout: 6000 });
    if (data && Array.isArray(data.iceServers) && data.iceServers.length) return data;
  } catch { /* fall through to static default */ }
  return RTC_CONFIG;
}

// Normalize whatever the backend returned (or nothing) into a usable RTCConfiguration.
export function iceConfigFrom(serverConfig) {
  if (serverConfig && Array.isArray(serverConfig.iceServers) && serverConfig.iceServers.length) {
    return { iceServers: serverConfig.iceServers, iceCandidatePoolSize: serverConfig.iceCandidatePoolSize ?? 4 };
  }
  return RTC_CONFIG;
}

export default RTC_CONFIG;
